/**
 * Unified "send message" modal. Replaces:
 *   - `send-message-modal.js::openSendMessageModal` (the 500-char rich textarea)
 *   - `chat-modal.js::openChatModal` (the 30-char SMS-only quick form)
 *
 * Uses the existing `#sendMessageModal` DOM from index.html. The 30-char
 * chat-modal flow is dropped — users always get the rich form. SMS is sent
 * automatically as part of `message.service.send()` if the recipient has
 * a phone on file.
 */

import { createLogger } from '../../core/logger.js';
import { format as formatPlate, normalize as normalizePlate } from '../../shared/plate.js';
import { escape } from '../../shared/escape.js';
import * as spinner from '../../core/spinner.js';
import * as alert from '../../core/alert.js';
import * as messageService from './message.service.js';
import * as conversationView from './conversation.view.js';
import * as phantomStore from '../phantom/phantom.store.js';
import * as phantomConfig from '../phantom/phantom.config.js';

const log = createLogger('send.view');

const IDS = /** @type {const} */ ({
  modal: 'sendMessageModal',
  closeX: 'sendMessageCloseX',
  closeBtn: 'sendMessageCloseBtn',
  form: 'sendMessageForm',
  textarea: 'userMessageContent',
  charCount: 'messageCharCount',
  result: 'sendMessageResult',
  plateDisplay: 'targetPlateDisplay',
  sharePhone: 'sharePhoneCheckbox',
});

let wired = false;
let currentTargetPlate = '';
/** Locks out re-entry while a `send()` is in flight — without this the user
 *  hits the submit button repeatedly (it looks idle because nothing on the
 *  button itself changes) and triggers multiple identical sends. */
let submitting = false;

/**
 * Open the send modal for the given recipient plate. Optional `prefillBody`
 * pre-fills the textarea (used for preset messages).
 *
 * @param {string} toPlate
 * @param {{ prefillBody?: string; sharePhone?: boolean; presetId?: string }} [opts]
 */
export function open(toPlate, opts = {}) {
  const plate = normalizePlate(toPlate);
  if (!plate) {
    void alert.showAlert({ title: 'שגיאה', message: 'מספר רכב לא תקין.' });
    return;
  }
  currentTargetPlate = plate;

  const modal = document.getElementById(IDS.modal);
  if (!modal) {
    log.warn('sendMessageModal element missing');
    return;
  }

  // The legacy index.html has `#sendMessageModal` *nested inside* `#accountDrawer`,
  // so it inherits the parent's `display:none` and is invisible until the
  // drawer happens to be open. Move it to <body> on first use so its
  // visibility and z-index stand on their own.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  // Plate display
  const plateDisplay = document.getElementById(IDS.plateDisplay);
  if (plateDisplay) plateDisplay.textContent = formatPlate(plate);

  // Pre-fill body
  const textarea = /** @type {HTMLTextAreaElement | null} */ (document.getElementById(IDS.textarea));
  if (textarea) {
    textarea.value = opts.prefillBody ?? '';
    updateCharCount(textarea.value.length);
  }

  // Default the share-phone checkbox to ON (matches legacy behavior). If the
  // caller passed `sharePhone: false` explicitly, respect that.
  const shareCb = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.sharePhone));
  if (shareCb) shareCb.checked = opts.sharePhone !== false;

  // Clear result
  setResult('', null);

  // Stash preset id; sharePhone is read live from the checkbox on submit.
  modal.dataset.presetId = opts.presetId ?? '';

  wireOnce();
  openOverlay(modal);
  textarea?.focus();
}

/** Close the modal. */
export function close() {
  const modal = document.getElementById(IDS.modal);
  if (modal) closeOverlay(modal);
}

// ====================================================================
// internals
// ====================================================================

function wireOnce() {
  if (wired) return;
  wired = true;
  const form = document.getElementById(IDS.form);
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void onSubmit();
    });
  }
  document.getElementById(IDS.closeX)?.addEventListener('click', close);
  document.getElementById(IDS.closeBtn)?.addEventListener('click', close);
  const textarea = /** @type {HTMLTextAreaElement | null} */ (document.getElementById(IDS.textarea));
  textarea?.addEventListener('input', () => updateCharCount(textarea.value.length));
}

async function onSubmit() {
  // Hard guard — a slow network used to let the user fire 3-4 sends before
  // any of them resolved. The global spinner isn't visible enough on this
  // modal, so the button needs to LOOK busy too.
  if (submitting) return;

  const textarea = /** @type {HTMLTextAreaElement | null} */ (document.getElementById(IDS.textarea));
  const body = (textarea?.value ?? '').trim();
  if (!body) {
    setResult('יש לכתוב הודעה.', 'error');
    return;
  }
  const modal = document.getElementById(IDS.modal);
  const shareCb = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.sharePhone));
  // Default to true if the checkbox isn't in the DOM (e.g. preset-only flow).
  const sharePhone = shareCb ? shareCb.checked : true;
  const presetId = modal?.dataset.presetId || undefined;

  submitting = true;
  const restoreBtn = setSubmitButtonBusy(true);
  const release = spinner.show('messaging.send');

  // Phantom branch: the recipient plate isn't registered to anyone. Store
  // the message in local-only `phantom.store` so it shows up in the user's
  // inbox; do NOT call `messageService.send` (which would try to dispatch
  // SMS + write to `conversations/*` and trigger the push Cloud Function
  // against a non-existent recipient). The UX is otherwise identical to a
  // successful real send.
  if (phantomConfig.isEnabled() && phantomStore.isPhantom(currentTargetPlate)) {
    const phantomPlate = currentTargetPlate;
    try {
      phantomStore.addPhantomMessage(phantomPlate, body);
    } finally {
      release();
      restoreBtn();
      submitting = false;
    }
    setResult('ההודעה נשלחה בהצלחה ✅', 'success');
    setTimeout(() => {
      close();
      void conversationView.open(phantomPlate, `phantom_${phantomPlate}`);
    }, 1000);
    return;
  }

  let result;
  try {
    result = await messageService.send({
      toPlate: currentTargetPlate,
      body,
      sharePhone,
      presetId,
    });
  } finally {
    release();
    restoreBtn();
    submitting = false;
  }

  if (result.status === 'ok') {
    const sentPlate = currentTargetPlate;
    // The in-app message saved successfully. The SMS to the driver's phone
    // is a separate "best effort" leg — if it failed (gateway rejected,
    // out of credits, etc.) we surface a soft warning instead of the green
    // checkmark, so the user knows their message reached the inbox but the
    // recipient didn't get a phone notification.
    if (result.smsError) {
      setResult('ההודעה נשלחה לתיבת הנמען, אך לא נשלחה לו הודעת SMS. הוא יראה אותה כשייכנס לאפליקציה.', 'success');
    } else {
      setResult('ההודעה נשלחה בהצלחה ✅', 'success');
    }
    setTimeout(() => {
      close();
      void conversationView.open(sentPlate);
    }, result.smsError ? 2200 : 1000);
    return;
  }
  setResult(result.message ?? 'שגיאה בשליחה.', 'error');
}

/**
 * Switch the submit button into a "busy" state — disabled, spinner-prefixed
 * label, dimmed. Returns a `restore()` function the caller invokes (in a
 * `finally`) to flip it back regardless of outcome.
 *
 * @param {boolean} busy
 */
function setSubmitButtonBusy(busy) {
  const form = document.getElementById(IDS.form);
  const btn = /** @type {HTMLButtonElement | null} */ (form?.querySelector('button[type="submit"]'));
  if (!btn) return () => {};

  if (!busy) return () => {};

  const originalHTML = btn.innerHTML;
  const originalDisabled = btn.disabled;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.style.opacity = '0.75';
  btn.style.cursor = 'wait';
  btn.innerHTML =
    '<span class="send-spinner" aria-hidden="true"></span>' +
    '<span>שולח הודעה...</span>';

  ensureSpinnerStyles();

  return () => {
    btn.innerHTML = originalHTML;
    btn.disabled = originalDisabled;
    btn.removeAttribute('aria-busy');
    btn.style.opacity = '';
    btn.style.cursor = '';
  };
}

/** Lazy-inject the keyframes + sizing for the inline spinner. The styles are
 *  scoped narrowly to `.send-spinner` so they don't bleed into anything else. */
let __sendSpinnerStyles = false;
function ensureSpinnerStyles() {
  if (__sendSpinnerStyles) return;
  __sendSpinnerStyles = true;
  const style = document.createElement('style');
  style.textContent = `
    .send-spinner {
      display: inline-block;
      width: 18px; height: 18px;
      border: 2.5px solid rgba(255, 255, 255, 0.35);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: send-spinner-rot 0.8s linear infinite;
      vertical-align: middle;
      margin-inline-end: 8px;
    }
    @keyframes send-spinner-rot { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);
}

/** @param {number} length */
function updateCharCount(length) {
  const el = document.getElementById(IDS.charCount);
  if (el) el.textContent = String(length);
}

/**
 * @param {string} text
 * @param {'error' | 'success' | null} kind
 */
function setResult(text, kind) {
  const el = document.getElementById(IDS.result);
  if (!el) return;
  if (!text) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  el.style.display = 'block';
  const color = kind === 'success' ? '#1e7e34' : '#a94442';
  el.style.color = color;
  el.innerHTML = escape(text);
}

/**
 * @param {HTMLElement} modal
 * The CSS rule `.drawer-overlay.open .drawer { opacity: 1 }` expects `.open`
 * on the OVERLAY element, not on the inner drawer. Adding it to the drawer
 * leaves the overlay visible (because we also set `display: flex` inline) but
 * invisibly captures clicks — looks "frozen" to the user.
 */
function openOverlay(modal) {
  modal.setAttribute('aria-hidden', 'false');
  modal.classList.add('open');
  modal.style.display = 'flex';
  document.body.classList.add('no-scroll');
}

/** @param {HTMLElement} modal */
function closeOverlay(modal) {
  modal.setAttribute('aria-hidden', 'true');
  modal.classList.remove('open');
  setTimeout(() => {
    if (modal.getAttribute('aria-hidden') === 'true') modal.style.display = 'none';
  }, 300);
  document.body.classList.remove('no-scroll');
}
